import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { api, type User } from '../api/client';
import { session } from './session';

export type SignInResult = 'ok' | 'invalid' | 'unreachable';

interface AuthState {
  user: User | null;
  signedIn: boolean;
  loading: boolean;
  signIn(username: string, password: string, remember: boolean): Promise<SignInResult>;
  signOut(): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const token = useSyncExternalStore(session.subscribe, session.token);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .GET('/v1/me')
      .then(({ data }) => {
        if (cancelled) return;
        setUser(data ?? null);
        setLoading(false);
      })
      .catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const signIn = useCallback(async (username: string, password: string, remember: boolean): Promise<SignInResult> => {
    try {
      const { data } = await api.POST('/v1/auth/login', { body: { username, password, rememberDevice: remember } });
      if (!data) return 'invalid';
      setUser(data.user);
      session.set(data.accessToken, remember);
      return 'ok';
    } catch {
      return 'unreachable';
    }
  }, []);

  const signOut = useCallback(() => session.clear(), []);

  return <AuthContext.Provider value={{ user, signedIn: Boolean(token), loading, signIn, signOut }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
